import { Controller } from '@hotwired/stimulus';
import graphql from 'stimulus/graphql';

// Owns the skills an employer proposes on this step: it routes the field's "not found" row
// to the modal that asks for one, and keeps the answers on the page as chips. They are not
// values of the field — a proposal is a ProposedTag awaiting review, saved against the job
// ad on its own — but the step's skill requirement counts them, so a step that is complete
// has to look complete.
const DELETE_PROPOSED_TAG = `
  mutation ($id: ID!) {
    deleteProposedTag(id: $id) {
      errors { field message }
    }
  }
`;

export default class extends Controller {
  static targets = ['chips', 'chipTemplate', 'error'];

  static outlets = ['propose-skill-modal-component', 'select-field-component'];

  open(event) {
    this.proposeSkillModalComponentOutlet.open(event.detail.query);
  }

  added({ detail: { value, label } }) {
    this.appendChip(value, label);
    this.chipsTarget.hidden = false;
    this.clearError();
    // The step asks for one skill and counts proposals towards it, so the answer the server
    // rendered the error about has just been given.
    if (this.hasSelectFieldComponentOutlet) this.selectFieldComponentOutlet.clearError();
  }

  async remove(event) {
    const value = String(event.params.value);
    const item = this.chipFor(value);
    if (!item) return;

    const button = event.currentTarget;
    button.disabled = true;
    this.clearError();

    try {
      await graphql(DELETE_PROPOSED_TAG, { id: value }, 'deleteProposedTag');
      item.remove();
      this.chipsTarget.hidden = !this.chipsTarget.querySelector('.jp-proposed-skills__item');
    } catch (error) {
      // The proposal is still on the job ad, so the chip stays and says why it did not go.
      this.showError(error.message);
      button.disabled = false;
    }
  }

  showError(message) {
    this.errorTarget.querySelector('[data-hint-text]').textContent = message;
    this.errorTarget.hidden = false;
  }

  clearError() {
    this.errorTarget.hidden = true;
  }

  appendChip(value, label) {
    const item = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    item.dataset.value = value;
    item.querySelector('.jp-proposed-skills__name').textContent = label;

    const removeButton = item.querySelector(`[data-${this.identifier}-target="removeButton"]`);
    removeButton.setAttribute('aria-label', `Remove ${label}`);
    removeButton.setAttribute(`data-${this.identifier}-value-param`, value);

    this.chipsTarget.appendChild(item);
  }

  chipFor(value) {
    return this.chipsTarget.querySelector(`.jp-proposed-skills__item[data-value="${CSS.escape(value)}"]`);
  }
}
